<template>
  <NavBar />
  <v-container style="margin-top: 185px;">
    <v-card>
      <v-card-title class="bg-primary text-white d-flex align-center">
        <v-icon class="mr-2">mdi-console</v-icon>
        {{ t.commands?.title || 'Commands' }}
      </v-card-title>

      <v-card-text class="pa-4">
        <!-- Search Filters -->
        <v-row>
          <!-- IMSI Search -->
          <v-col cols="12" md="2">
            <v-text-field
              v-model="searchImsi"
              :label="t.commands?.search_imsi || 'Search IMSI'"
              prepend-inner-icon="mdi-numeric"
              clearable
              density="compact"
              hide-details
              @keyup.enter="loadCommands"
            ></v-text-field>
          </v-col>

          <!-- MSISDN Search -->
          <v-col cols="12" md="2">
            <v-text-field
              v-model="searchMsisdn"
              :label="t.commands?.search_msisdn || 'Search MSISDN'"
              prepend-inner-icon="mdi-phone"
              clearable
              density="compact"
              hide-details
              @keyup.enter="loadCommands"
            ></v-text-field>
          </v-col>

          <!-- SIM/ICCID Search -->
          <v-col cols="12" md="2">
            <v-text-field
              v-model="searchSim"
              :label="t.commands?.search_sim || 'Search SIM ID'"
              prepend-inner-icon="mdi-sim"
              clearable
              density="compact"
              hide-details
              @keyup.enter="loadCommands"
            ></v-text-field>
          </v-col>

          <!-- Client Filter -->
          <v-col cols="12" md="2">
            <v-autocomplete
              v-model="selectedClientId"
              :items="clientOptions"
              item-value="id"
              item-title="name"
              :label="t.common?.client || 'Client'"
              density="compact"
              hide-details
              clearable
            ></v-autocomplete>
          </v-col>

          <!-- Command Status Filter -->
          <v-col cols="12" md="2">
            <v-select
              v-model="selectedStatusId"
              :items="commandStatusOptions"
              item-value="id"
              item-title="name"
              :label="t.common?.status || 'Status'"
              density="compact"
              hide-details
              clearable
            ></v-select>
          </v-col>

          <!-- Search Button -->
          <v-col cols="12" md="1" class="d-flex align-center">
            <v-btn color="primary" @click="loadCommands" :loading="loading">
              <v-icon>mdi-magnify</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <!-- Advanced Filters (collapsed by default) -->
        <v-expansion-panels class="mt-3" v-model="advancedFiltersPanel">
          <v-expansion-panel>
            <v-expansion-panel-title>
              <v-icon class="mr-2" size="small">mdi-filter-variant</v-icon>
              {{ t.common?.advanced_filters || 'Advanced Filters' }}
            </v-expansion-panel-title>
            <v-expansion-panel-text>
              <v-row>
                <!-- Provision Action -->
                <v-col cols="12" md="3">
                  <v-select
                    v-model="selectedProvisionActionId"
                    :items="provisionActionOptions"
                    item-value="id"
                    item-title="name"
                    :label="t.commands?.provision_action || 'Provision Action'"
                    density="compact"
                    hide-details
                    clearable
                  ></v-select>
                </v-col>

                <!-- Provision Use Case -->
                <v-col cols="12" md="3">
                  <v-select
                    v-model="selectedProvisionUseCaseId"
                    :items="provisionUseCaseOptions"
                    item-value="id"
                    item-title="name"
                    :label="t.commands?.provision_use_case || 'Provision Use Case'"
                    density="compact"
                    hide-details
                    clearable
                  ></v-select>
                </v-col>

                <!-- Date From -->
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="dateFrom"
                    :label="t.common?.date_from || 'Date From'"
                    type="date"
                    density="compact"
                    hide-details
                    clearable
                  ></v-text-field>
                </v-col>

                <!-- Date To -->
                <v-col cols="12" md="3">
                  <v-text-field
                    v-model="dateTo"
                    :label="t.common?.date_to || 'Date To'"
                    type="date"
                    density="compact"
                    hide-details
                    clearable
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-divider class="my-4"></v-divider>

        <!-- Results Count -->
        <div class="text-caption mb-2" v-if="!loading">
          {{ t.common?.showing || 'Showing' }} {{ commands.length }} {{ t.common?.of || 'of' }} {{ pagination.total_count }} {{ t.commands?.commands || 'commands' }}
        </div>

        <!-- Commands Table -->
        <v-data-table
          :headers="headers"
          :items="commands"
          :loading="loading"
          :items-per-page="pagination.per_page"
          :page="pagination.page"
          density="compact"
          class="elevation-1"
          show-expand
          item-value="id"
          @update:page="onPageChange"
        >
          <!-- Command ID -->
          <template v-slot:[`item.id`]="{ item }">
            <span class="font-weight-bold">#{{ item.id }}</span>
          </template>

          <!-- Created At -->
          <template v-slot:[`item.created_at`]="{ item }">
            {{ formatDate(item.created_at) }}
          </template>

          <!-- IMSI Value -->
          <template v-slot:[`item.imsi_value`]="{ item }">
            <code v-if="item.imsi_value">{{ item.imsi_value }}</code>
            <span v-else class="text-grey">-</span>
          </template>

          <!-- MSISDN Value -->
          <template v-slot:[`item.msisdn_value`]="{ item }">
            <code v-if="item.msisdn_value">{{ item.msisdn_value }}</code>
            <span v-else class="text-grey">-</span>
          </template>

          <!-- SIM ICCID -->
          <template v-slot:[`item.sim_iccid`]="{ item }">
            <code v-if="item.sim_iccid">{{ item.sim_iccid }}</code>
            <span v-else class="text-grey">-</span>
          </template>

          <!-- Provision Action -->
          <template v-slot:[`item.provision_action_name`]="{ item }">
            <v-chip
              v-if="item.provision_action_name"
              size="x-small"
              :color="getActionColor(item.provision_action_name)"
            >
              {{ item.provision_action_name }}
            </v-chip>
            <span v-else class="text-grey">-</span>
          </template>

          <!-- Provision Use Case -->
          <template v-slot:[`item.provision_use_case_name`]="{ item }">
            <v-chip
              v-if="item.provision_use_case_name"
              size="x-small"
              color="info"
              variant="outlined"
            >
              {{ item.provision_use_case_name }}
            </v-chip>
            <span v-else class="text-grey">-</span>
          </template>

          <!-- Command Status -->
          <template v-slot:[`item.command_status_name`]="{ item }">
            <v-chip
              size="small"
              :color="getStatusColor(item.command_status_name)"
            >
              {{ item.command_status_name }}
            </v-chip>
          </template>

          <!-- Client Name -->
          <template v-slot:[`item.client_name`]="{ item }">
            {{ item.client_name || '-' }}
          </template>

          <!-- Live/Test Mode -->
          <template v-slot:[`item.live_medium`]="{ item }">
            <v-chip
              size="x-small"
              :color="item.live_medium ? 'success' : 'warning'"
            >
              {{ item.live_medium ? 'LIVE' : 'TEST' }}
            </v-chip>
          </template>

          <!-- Actions -->
          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex ga-1">
              <v-btn
                size="x-small"
                color="info"
                variant="outlined"
                @click="openDetailsDialog(item)"
              >
                <v-icon size="small">mdi-eye</v-icon>
              </v-btn>
            </div>
          </template>

          <template v-slot:[`item.data-table-expand`]="{ toggleExpand, isExpanded, internalItem }">
            <v-btn icon variant="text" size="small" @click="toggleExpand(internalItem)">
              <v-icon :color="isExpanded(internalItem) ? 'primary' : ''">mdi-eye{{ isExpanded(internalItem) ? '' : '-off-outline' }}</v-icon>
            </v-btn>
          </template>

          <!-- Expanded Row -->
          <template v-slot:expanded-row="{ columns, item }">
            <tr>
              <td :colspan="columns.length" class="pa-0">
                <v-card flat class="ma-2 bg-grey-lighten-4">
                  <v-card-title class="text-subtitle-2 py-2 bg-grey-lighten-3">
                    <v-icon size="small" class="mr-2">mdi-information</v-icon>
                    {{ t.commands?.details || 'Command Details' }}
                  </v-card-title>
                  <v-card-text class="pa-3">
                    <v-row>
                      <v-col cols="12" md="3">
                        <div class="text-caption text-grey">Request ID</div>
                        <code v-if="item.request_id">{{ item.request_id }}</code>
                        <span v-else>-</span>
                      </v-col>
                      <v-col cols="12" md="3">
                        <div class="text-caption text-grey">MVNO</div>
                        <span>{{ item.mvno_name || '-' }}</span>
                      </v-col>
                      <v-col cols="12" md="3">
                        <div class="text-caption text-grey">Product Code</div>
                        <span>{{ item.product_code_name || '-' }}</span>
                      </v-col>
                      <v-col cols="12" md="3">
                        <div class="text-caption text-grey">Subscription Type</div>
                        <span>{{ item.subscription_type_name || '-' }}</span>
                      </v-col>
                    </v-row>
                    <v-row class="mt-2">
                      <v-col cols="12" md="3">
                        <div class="text-caption text-grey">ACK Status Code</div>
                        <code v-if="item.ack_status_response_code">{{ item.ack_status_response_code }}</code>
                        <span v-else>-</span>
                      </v-col>
                      <v-col cols="12" md="3">
                        <div class="text-caption text-grey">Reason Status</div>
                        <span>{{ item.reason_status || '-' }}</span>
                      </v-col>
                      <v-col cols="12" md="3">
                        <div class="text-caption text-grey">Callback Received</div>
                        <v-icon :color="item.callback_received ? 'success' : 'grey'" size="small">
                          {{ item.callback_received ? 'mdi-check-circle' : 'mdi-close-circle' }}
                        </v-icon>
                      </v-col>
                      <v-col cols="12" md="3">
                        <div class="text-caption text-grey">Created By</div>
                        <span>{{ item.user_name || '-' }}</span>
                      </v-col>
                    </v-row>
                  </v-card-text>
                </v-card>
              </td>
            </tr>
          </template>
        </v-data-table>

        <!-- Pagination -->
        <v-pagination
          v-if="pagination.total_pages > 1"
          v-model="pagination.page"
          :length="pagination.total_pages"
          :total-visible="7"
          class="mt-4"
          @update:model-value="loadCommands"
        ></v-pagination>
      </v-card-text>
    </v-card>

    <!-- Command Details Dialog -->
    <v-dialog v-model="detailsDialog" max-width="800">
      <v-card v-if="selectedCommand">
        <v-card-title class="bg-primary text-white d-flex align-center">
          <v-icon class="mr-2">mdi-console</v-icon>
          {{ t.commands?.command_details || 'Command Details' }} #{{ selectedCommand.id }}
        </v-card-title>
        <v-card-text class="pa-4">
          <v-row>
            <!-- Basic Info -->
            <v-col cols="12" md="6">
              <v-table density="compact">
                <tbody>
                  <tr>
                    <td class="font-weight-medium" width="40%">ID</td>
                    <td>#{{ selectedCommand.id }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">{{ t.common?.created_at || 'Created At' }}</td>
                    <td>{{ formatDate(selectedCommand.created_at) }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">{{ t.common?.status || 'Status' }}</td>
                    <td>
                      <v-chip size="small" :color="getStatusColor(selectedCommand.command_status_name)">
                        {{ selectedCommand.command_status_name }}
                      </v-chip>
                    </td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">{{ t.common?.client || 'Client' }}</td>
                    <td>{{ selectedCommand.client_name || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">{{ t.common?.created_by || 'Created By' }}</td>
                    <td>{{ selectedCommand.user_name || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Mode</td>
                    <td>
                      <v-chip size="x-small" :color="selectedCommand.live_medium ? 'success' : 'warning'">
                        {{ selectedCommand.live_medium ? 'LIVE' : 'TEST' }}
                      </v-chip>
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>

            <!-- Resource Info -->
            <v-col cols="12" md="6">
              <v-table density="compact">
                <tbody>
                  <tr>
                    <td class="font-weight-medium" width="40%">IMSI</td>
                    <td><code v-if="selectedCommand.imsi_value">{{ selectedCommand.imsi_value }}</code><span v-else>-</span></td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">MSISDN</td>
                    <td><code v-if="selectedCommand.msisdn_value">{{ selectedCommand.msisdn_value }}</code><span v-else>-</span></td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">SIM ICCID</td>
                    <td><code v-if="selectedCommand.sim_iccid">{{ selectedCommand.sim_iccid }}</code><span v-else>-</span></td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Action</td>
                    <td>
                      <v-chip v-if="selectedCommand.provision_action_name" size="x-small" :color="getActionColor(selectedCommand.provision_action_name)">
                        {{ selectedCommand.provision_action_name }}
                      </v-chip>
                      <span v-else>-</span>
                    </td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Use Case</td>
                    <td>{{ selectedCommand.provision_use_case_name || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Request ID</td>
                    <td><code v-if="selectedCommand.request_id" class="text-caption">{{ selectedCommand.request_id }}</code><span v-else>-</span></td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>
          </v-row>

          <v-divider class="my-4"></v-divider>

          <!-- Response Info -->
          <div class="text-subtitle-2 mb-2">Response Information</div>
          <v-row>
            <v-col cols="12" md="6">
              <v-table density="compact">
                <tbody>
                  <tr>
                    <td class="font-weight-medium" width="40%">ACK Code</td>
                    <td><code v-if="selectedCommand.ack_status_response_code">{{ selectedCommand.ack_status_response_code }}</code><span v-else>-</span></td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Reason</td>
                    <td>{{ selectedCommand.reason_status || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Callback</td>
                    <td>
                      <v-icon :color="selectedCommand.callback_received ? 'success' : 'grey'" size="small">
                        {{ selectedCommand.callback_received ? 'mdi-check-circle' : 'mdi-close-circle' }}
                      </v-icon>
                      {{ selectedCommand.callback_received ? 'Received' : 'Pending' }}
                    </td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>
            <v-col cols="12" md="6">
              <v-table density="compact">
                <tbody>
                  <tr>
                    <td class="font-weight-medium" width="40%">MVNO</td>
                    <td>{{ selectedCommand.mvno_name || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Product Code</td>
                    <td>{{ selectedCommand.product_code_name || '-' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-medium">Subscription Type</td>
                    <td>{{ selectedCommand.subscription_type_name || '-' }}</td>
                  </tr>
                </tbody>
              </v-table>
            </v-col>
          </v-row>

          <!-- Details/Notes -->
          <div v-if="selectedCommand.details" class="mt-4">
            <div class="text-subtitle-2 mb-2">{{ t.common?.details || 'Details' }}</div>
            <v-card variant="outlined" class="pa-3 bg-grey-lighten-4">
              <pre class="text-caption">{{ selectedCommand.details }}</pre>
            </v-card>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" variant="flat" @click="detailsDialog = false">
            {{ t.common?.close || 'Close' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { defineComponent } from 'vue';
import { log } from "@/composables/useLog";
import { getCommands, getCommandDetails, getCommandFilters } from "@/composables/useCommandsAPI";
import store from "@/store";
import { t as tRef } from "@/composables/useLanguages";
import NavBar from "@/components/NavBar.vue";

export default defineComponent({
  name: 'CommandsView',
  components: { NavBar },
  data() {
    return {
      loading: false,
      token: '',

      // Filters
      searchImsi: '',
      searchMsisdn: '',
      searchSim: '',
      selectedClientId: null,
      selectedStatusId: null,
      selectedProvisionActionId: null,
      selectedProvisionUseCaseId: null,
      dateFrom: '',
      dateTo: '',
      advancedFiltersPanel: null,

      // Filter options (loaded from API)
      commandStatuses: [],
      provisionActions: [],
      provisionUseCases: [],
      clients: [],

      // Data
      commands: [],

      // Pagination
      pagination: {
        page: 1,
        per_page: 50,
        total_count: 0,
        total_pages: 0
      },

      // Details dialog
      detailsDialog: false,
      selectedCommand: null
    };
  },
  computed: {
    t() {
      return tRef.value;
    },
    headers() {
      return [
        { title: 'ID', key: 'id', sortable: true, width: '70px' },
        { title: this.t.common?.created_at || 'Created At', key: 'created_at', sortable: true, width: '150px' },
        { title: 'IMSI', key: 'imsi_value', sortable: true },
        { title: 'MSISDN', key: 'msisdn_value', sortable: true },
        { title: 'SIM', key: 'sim_iccid', sortable: false },
        { title: this.t.commands?.action || 'Action', key: 'provision_action_name', sortable: true, width: '100px' },
        { title: this.t.commands?.use_case || 'Use Case', key: 'provision_use_case_name', sortable: true },
        { title: this.t.common?.status || 'Status', key: 'command_status_name', sortable: true, width: '120px' },
        { title: this.t.common?.client || 'Client', key: 'client_name', sortable: true },
        { title: 'Mode', key: 'live_medium', sortable: true, width: '80px' },
        { title: '', key: 'actions', sortable: false, width: '60px' },
        { title: '', key: 'data-table-expand', width: '50px' }
      ];
    },
    commandStatusOptions() {
      return this.commandStatuses;
    },
    provisionActionOptions() {
      return this.provisionActions;
    },
    provisionUseCaseOptions() {
      return this.provisionUseCases;
    },
    clientOptions() {
      return this.clients;
    }
  },
  methods: {
    async loadCommands() {
      this.loading = true;
      try {
        const params = {
          page: this.pagination.page,
          per_page: this.pagination.per_page
        };

        if (this.searchImsi) {
          params.imsi_value = this.searchImsi;
        }
        if (this.searchMsisdn) {
          params.msisdn_value = this.searchMsisdn;
        }
        if (this.searchSim) {
          params.sim_id = this.searchSim;
        }
        if (this.selectedClientId) {
          params.client_id = this.selectedClientId;
        }
        if (this.selectedStatusId) {
          params.command_status_id = this.selectedStatusId;
        }
        if (this.selectedProvisionActionId) {
          params.provision_action_id = this.selectedProvisionActionId;
        }
        if (this.selectedProvisionUseCaseId) {
          params.provision_use_case_id = this.selectedProvisionUseCaseId;
        }
        if (this.dateFrom) {
          params.date_from = this.dateFrom;
        }
        if (this.dateTo) {
          params.date_to = this.dateTo;
        }

        const response = await getCommands(this.token, params);

        if (response.error) {
          log("Error loading commands: " + response.message);
          this.commands = [];
        } else {
          this.commands = response.commands || [];
          this.pagination.total_count = response.total_count || 0;
          this.pagination.total_pages = response.total_pages || 0;
          this.pagination.page = response.page || 1;
        }
      } catch (err) {
        log("Exception loading commands:");
        log(err);
        this.commands = [];
      } finally {
        this.loading = false;
      }
    },

    async loadFilters() {
      try {
        const response = await getCommandFilters(this.token);
        if (!response.error) {
          this.commandStatuses = response.command_statuses || [];
          this.provisionActions = response.provision_actions || [];
          this.provisionUseCases = response.provision_use_cases || [];
          this.clients = response.clients || [];
        }
      } catch (err) {
        log("Error loading command filters:");
        log(err);
      }
    },

    onPageChange(page) {
      this.pagination.page = page;
      this.loadCommands();
    },

    async openDetailsDialog(item) {
      try {
        const response = await getCommandDetails(this.token, item.id);
        if (!response.error) {
          this.selectedCommand = response.command;
          this.detailsDialog = true;
        }
      } catch (err) {
        log("Error loading command details:");
        log(err);
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return '-';
      const date = new Date(dateStr);
      return date.toLocaleDateString('ro-RO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    getStatusColor(status) {
      const colors = {
        'pending': 'warning',
        'processing': 'info',
        'completed': 'success',
        'failed': 'error',
        'cancelled': 'grey'
      };
      return colors[status?.toLowerCase()] || 'grey';
    },

    getActionColor(action) {
      const colors = {
        'add': 'success',
        'delete': 'error',
        'modify': 'info',
        'terminate': 'error',
        'suspend': 'orange',
        'reactivate': 'success'
      };
      return colors[action?.toLowerCase()] || 'grey';
    }
  },
  async mounted() {
    this.token = store.getters.getToken;
    await this.loadFilters();
    await this.loadCommands();
  }
});
</script>

<style scoped>
.v-table th {
  font-weight: 600 !important;
  white-space: nowrap;
}
code {
  background-color: #f5f5f5;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 0.85em;
}
pre {
  white-space: pre-wrap;
  word-wrap: break-word;
  margin: 0;
}
</style>
